import { useMutation, useQuery } from "@tanstack/react-query";
import { AvForm, AvInput } from "availity-reactstrap-validation";
import { useCallback, useEffect, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import { RiErrorWarningLine, RiInformationLine } from "react-icons/ri";
import { toast } from "react-toastify";
import { Alert, Col, Label, Row, Spinner } from "reactstrap";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import { ControlsFrameworks } from "src/modules/data-protection/constants";
import Select, { components } from "react-select";
import CommonService from "src/modules/data-protection/apis/CommonService";
import { connect } from "react-redux";

const ComplianceFrameworks = ({ t, organization }) => {
    const formRef = useRef();
    const [ displayAtLeastOneFrameworkError, setDisplayAtLeastOneFrameworkError ] = useState(false);

    const [ frameworks, setFrameworks ] = useState(ControlsFrameworks.map((framework) => ({
        ...framework,
        enabled: false,
        isLoading: false,
        entities: []
    })));

    const fetchFrameworkSettingsQuery = useQuery({
        queryKey: ['data-protection-fetch-compliance-frameworks-settings'],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchSettings();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load framework settings. Please try again.'), {
                type: "error"
            });
        }
    });

    const {
        mutate: handleActivateFramework
    } = useMutation({
        mutationFn: async (payload) => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.activateFramework(payload);
        },
        onSuccess: (_, { framework }) => {
            toast.success(t("Framework activated successfully."));

            setFrameworks((prev) => {
                return [...prev].map((item) => ({
                    ...item,
                    enabled: item.key === framework ? true : item.enabled,
                }))
            });
        },
        onError: (_, { framework }) => {
            if (formRef.current) {
                formRef.current._inputs[framework].reset();
            }

            toast.error(t("An error occurred while activating framework."));
        },
        onSettled: (_, __, { framework }) => {
            setFrameworks((prev) => {
                return [...prev].map((item) => ({
                    ...item,
                    isLoading: item.key === framework ? false : item.isLoading,
                }))
            });
        }
    });

    const {
        mutate: handleDeactivateFramework
    } = useMutation({
        mutationFn: async (payload) => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.deactivateFramework(payload);
        },
        onSuccess: (_, { framework }) => {
            toast.success(t("Framework deactivated successfully."));

            setFrameworks((prev) => {
                return [...prev].map((item) => ({
                    ...item,
                    enabled: item.key === framework ? false : item.enabled,
                }))
            });
        },
        onError: ({ response }, { framework }) => {
            if (response?.data?.message?.toLowerCase() === "at least one framework must remain enabled") { 
                setDisplayAtLeastOneFrameworkError(true);
            }

            if (formRef.current) {
                formRef.current._inputs[framework].reset();
            }

            toast.error(t("An error occurred while deactivating framework."));
        },
        onSettled: (_, __, { framework }) => {
            setFrameworks((prev) => {
                return [...prev].map((item) => ({
                    ...item,
                    isLoading: item.key === framework ? false : item.isLoading,
                }))
            });
        }
    });

    const {
        mutate: handleAddLegalEntitiesToFramework
    } = useMutation({
        mutationFn: async (payload) => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.addLegalToFramework(payload);
        },
        onSuccess: (_, item) => {
            toast.success(t("Legal entities added to framework successfully."));
        },
        onError: () => {
            toast.error(t("An error occurred while adding legal entities to framework."));
        }
    });

    const {
        mutate: handleRemoveLegalEntitiesFromFramework
    } = useMutation({
        mutationFn: async (payload) => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.removeLegalFromFramework(payload);
        },
        onSuccess: () => {
            toast.success(t("Legal entities removed from framework successfully."));
        },
        onError: () => {
            toast.error(t("An error occurred while removing legal entities from framework."));
        }
    });

    const {
        data: legalEntitiesOptions = [],
        isFetching: isFetchingLegalEntities
    } = useQuery({
        queryKey: ["data-protection-enteties-list"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            const items = await service.legalEntities(organization.id);

            return ([{
                title: t("All legal entities"),
                id: "all"
            }, ...(items || [])]).map((item) => ({
                label: item.title,
                value: item.id
            }));
        },
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching legal entities."), {
                type: "error"
            });
        },
    });

    useEffect(() => {
        const frameWorksDetails = fetchFrameworkSettingsQuery?.data?.framework_details;

        if (frameWorksDetails) {
            setFrameworks((prev) => {
                return [...prev].map((prevFramework) => {
                    const isEnabled = frameWorksDetails[prevFramework.key]?.enabled;
                    const legalEntities = frameWorksDetails[prevFramework.key]?.legal_entities;
                    let legalItemOptions = [];

                    if (legalEntities === 'all') {
                        legalItemOptions.push({
                            label: t("All legal entities"),
                            value: 'all'
                        })
                    }
                    else {
                        legalItemOptions = legalEntitiesOptions.filter((option) => legalEntities.includes(option.value)).map((entity) => ({
                            label: entity.label,
                            value: entity.value
                        }));
                    }

                    return {
                        ...prevFramework,
                        enabled: isEnabled,
                        entities: legalItemOptions
                    }
                })
            })
        }
    }, [
        fetchFrameworkSettingsQuery?.data,
        legalEntitiesOptions,
        t
    ]);

    const handleFormChange = useCallback((e) => {
        const target = e.target;
        const name = target.name;
        const checked = target.checked;

        if (checked) {
            setFrameworks((prev) => {
                return [...prev].map((item) => ({
                    ...item,
                    isLoading: item.key === name ? true : item.isLoading,
                }))
            });

            handleActivateFramework({
                framework: name
            });
        } else {
            setFrameworks((prev) => {
                return [...prev].map((item) => ({
                    ...item,
                    isLoading: item.key === name ? true : item.isLoading,
                }))
            });

            handleDeactivateFramework({
                framework: name
            });
        }        
    }, [
        handleActivateFramework,
        handleDeactivateFramework
    ]);

    const MultiValueRemove = (props) => {
        const isLoading = props.data.isLoading; 
        return (
            <components.MultiValueRemove {...props}>
                {isLoading && <Spinner color="secondary" size="sm" />}
            </components.MultiValueRemove>
        );
    };

    const updateFrameworkEntityLoadingStatus = (framework, entityId, status) => {
        setFrameworks((prev) => {
            return [...prev].map((item) => ({
                ...item,
                entities: item.key === framework ? (
                    item.entities.map((entity) => {
                        return {
                            ...entity,
                            isLoading: entity.value === entityId ? status : entity.isLoading
                        }
                    })
                ) : item.entities
            }))
        });
    }

    const removeEntityFromFramework = (framework, entity) => {
        setFrameworks((prev) => {
            return [...prev].map((frameItem) => {
                if (frameItem.key === framework) {
                    return {
                        ...frameItem,
                        entities: frameItem.entities.filter((entityItem) => entityItem.value !== entity)
                    }
                }
                else {
                    return frameItem
                }
            })
        });
    }

    const addEntityToFramework = (framework, entityOption) => {
        setFrameworks((prev) => {
            return [...prev].map((frameItem) => {
                if (frameItem.key === framework) {
                    return {
                        ...frameItem,
                        entities: entityOption.value === 'all' ? 
                            [entityOption] : 
                            [...frameItem.entities.filter((ent) => ent.value !== 'all'), entityOption]
                    }
                }
                else {
                    return frameItem
                }
            })
        })
    }

    const handleChangeLegalEntities = (selectedOptions, e) => {
        const framework = e.name;
        
        if(e.action === 'remove-value'){
            const removedLegalEntity = e.removedValue.value;

            updateFrameworkEntityLoadingStatus(framework, removedLegalEntity, true);
            
            handleRemoveLegalEntitiesFromFramework({
                framework,
                legal_entity: removedLegalEntity
            }, {
                onSuccess: (__, { legal_entity }) => {
                    removeEntityFromFramework(framework, legal_entity);
                },
                onError: (__, { legal_entity }) => {
                    updateFrameworkEntityLoadingStatus(framework, legal_entity, false);
                }
            });
        }
        else {
            handleAddLegalEntitiesToFramework({
                framework,
                legal_entity: e.option.value
            }, {
                onSuccess: () => {
                    addEntityToFramework(framework, e.option)
                }
            });
        }
    }

    return (
        <Row>
            <Col 
                sm="12" 
                md='12' 
                lg="12"
            >
                <AvForm
                    className="needs-validation"
                    onChange={handleFormChange}
                    ref={formRef}
                >
                    <Row>
                        <Col sm="12">
                            <Alert 
                                color="info"
                                className="d-flex align-items-start"
                            >
                                <RiInformationLine size="32" className="me-2 mt-1" />

                                <div>
                                    {t('You can configure ISMS Frameworks by selecting or deselecting individual controls and assigning legal entities below.')} {t('All changes are applied immediately. There is no need to save — updates are processed in real time as you make them.')}
                                </div>
                            </Alert>
                        </Col>

                        {frameworks.map((framework, index) => {
                            return (
                                <Col 
                                    sm="12" 
                                    className="mb-3"
                                    key={index}
                                >
                                    {fetchFrameworkSettingsQuery.isFetching ? (
                                        <Row>
                                            <Col sm="2" md="1">
                                                <div
                                                    className="dt-field dt-skeleton"
                                                    style={{ width: '100%' }}
                                                />
                                            </Col>

                                            <Col sm="10" md="11">
                                                <div
                                                    className="dt-field dt-skeleton"
                                                    style={{ width: '100%' }}
                                                />
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row
                                            className="mb-3"
                                        >
                                            <Col sm="12">
                                                <div
                                                    className="form-check form-switch"
                                                >
                                                    <AvInput
                                                        id={`framework-${framework.key}`}
                                                        type="checkbox"
                                                        name={framework.key}
                                                        disabled={framework.isLoading}
                                                        value={framework.enabled}
                                                        checked={framework.enabled}
                                                    />

                                                    <Label
                                                        htmlFor={`framework-${framework.key}`}
                                                        className="form-check-label"
                                                    >
                                                        {framework.isLoading && (
                                                            <Spinner
                                                                size="sm"
                                                                color="primary"
                                                                className="me-1"
                                                            />
                                                        )}

                                                        {framework.title}
                                                    </Label>
                                                </div>
                                            </Col>

                                            <Col
                                                sm="12" md="12" lg="12" xl="6"
                                                className="mt-2"
                                            >
                                                <div
                                                    className="ml-4 "
                                                >
                                                    <Label 
                                                        for={`framework-${framework.key}-legal-entities`}
                                                    >
                                                        {t("Legal Entities")}: 
                                                    </Label>
                                                    <Select
                                                        isDisabled={!framework.enabled}
                                                        name={framework.key}
                                                        isClearable={false}
                                                        isMulti
                                                        isLoading={isFetchingLegalEntities || framework.isLoading}
                                                        placeholder={t("Select") + "..."}
                                                        classNamePrefix="select2-selection"
                                                        id={`framework-${framework.key}-legal-entities`}
                                                        options={legalEntitiesOptions}
                                                        menuPortalTarget={document.body}
                                                        onChange={handleChangeLegalEntities}
                                                        components={{ MultiValueRemove }}
                                                        filterOption={(e) => e}
                                                        value={framework.entities}
                                                        onInputChange={(input) => {
                                                            // console.log(input)
                                                        }}
                                                        backspaceRemovesValue={false}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            )
                        })}

                        {displayAtLeastOneFrameworkError && (
                            <Col 
                                sm="12" 
                                className="d-flex align-items-center justify-content-start gap-1 text-danger"
                            >
                                <RiErrorWarningLine 
                                    size="20" 
                                />
                                <span>
                                    {t('At least one compliance framework must be enabled.')}
                                </span>
                            </Col>
                        )}
                    </Row>
                </AvForm>
            </Col>
        </Row>
    )
};

const mapStatetoProps = (state) => {
    const { Organization } = state;
    return {
        organization: Organization
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        ComplianceFrameworks
    )
);
